import "core-js/modules/es6.array.for-each";
import _differenceBy from "lodash/differenceBy";
import _pick from "lodash/pick";
import _forEach from "lodash/forEach";
import _get from "lodash/get";
import moment from 'moment';
import analytics from '@nerdwallet/analytics';
import track from "./track";
import { getAllProviderAccounts, getIdfromProviderAccount, getProviderAccountById, getProviderAccountStateIdFromProviderAccount, getStateFromProviderAccount, getStateSetAtFromProviderAccount, getStatusCodeFromProviderAccount } from "../../selectors/yodlee";
import { ProviderAccountState } from "../../constants";
import { getPrimarySourceFromProviderAccount } from "../../utils/yodlee";
var providerAccountAllowList = ['id', 'identity_id', 'is_active_sync', 'previous_state', 'primary_source', 'provider_account_id', 'provider_id', 'provider_name', 'state', 'status_code', 'status_message'];

var hasStateChanged = function hasStateChanged(prevProviderAccount, nextProviderAccount) {
  var prevProviderState = getStateFromProviderAccount(prevProviderAccount);
  var nextProviderState = getStateFromProviderAccount(nextProviderAccount);
  var prevStatusCode = getStatusCodeFromProviderAccount(prevProviderAccount);
  var nextStatusCode = getStatusCodeFromProviderAccount(nextProviderAccount); // if no next provider account - then not a state change. Provider account has likely been
  // deselected at the end of onboarding

  if (!nextProviderAccount) return false; // if no previous provider account this is likely an existing provider account being selected
  // so don't treat as a state change.
  // Exception: if new provider account state is LOGIN_IN_PROGRESS then treat it as as state change

  if (!prevProviderAccount && nextProviderState !== ProviderAccountState.LOGIN_IN_PROGRESS) {
    return false;
  } // true if either provider account state or status code has changed, e.g. still on error
  // state but it's a new error


  return prevProviderState !== nextProviderState || prevStatusCode !== nextStatusCode;
};

var didProviderAccountFinishLinking = function didProviderAccountFinishLinking(prevProviderAccount, nextProviderAccount) {
  var prevProviderState = getStateFromProviderAccount(prevProviderAccount);
  var nextProviderState = getStateFromProviderAccount(nextProviderAccount);
  return (prevProviderState === ProviderAccountState.LOGIN_IN_PROGRESS || prevProviderState === ProviderAccountState.MFA_IN_PROGRESS) && (nextProviderState === ProviderAccountState.AUTHENTICATED || nextProviderState === ProviderAccountState.ACTIVE);
};

var getPreviousStateDuration = function getPreviousStateDuration(prevProviderAccount, nextProviderAccount) {
  if (!prevProviderAccount || !nextProviderAccount) return null;
  var prevTime = moment(getStateSetAtFromProviderAccount(prevProviderAccount), moment.ISO_8601).unix();
  var nextTime = moment(getStateSetAtFromProviderAccount(nextProviderAccount), moment.ISO_8601).unix();
  return nextTime - prevTime;
};

export default (function (prevState, nextState) {
  var prevProviderAccounts = getAllProviderAccounts(prevState.identity);
  var nextProviderAccounts = getAllProviderAccounts(nextState.identity);

  var trackingState = _get(nextState, 'identity.tracking', null);

  var numPreviousProviderAccounts = prevProviderAccounts.length;
  var numNextProviderAccounts = nextProviderAccounts.length;

  _forEach(nextProviderAccounts, function (nextProviderAccount) {
    var nextId = getIdfromProviderAccount(nextProviderAccount);
    var prevProviderAccount = getProviderAccountById(prevState.identity, nextId);

    if (hasStateChanged(prevProviderAccount, nextProviderAccount)) {
      var data = _pick(nextProviderAccount.provider_account_state, providerAccountAllowList);

      data.previousStateDuration = getPreviousStateDuration(prevProviderAccount, nextProviderAccount);
      track('provider-account:state-change', trackingState, data); // Wait until the linking is successful before firing (to avoid credentials failure)

      if (didProviderAccountFinishLinking(prevProviderAccount, nextProviderAccount)) {
        var _prevProviderAccount$, _prevProviderAccount$2;

        analytics.track('financial_account_aggregation_enrollment_changed', {
          enrollment_change_type: // the PA will exist before successfully linking, so check if count > 1 instead of 0
          numPreviousProviderAccounts > 1 ? 'increase_enrollment' : 'enroll',
          feature_name: 'financial_account_aggregation',
          additional_attributes: {
            financial_portal_id: getProviderAccountStateIdFromProviderAccount(prevProviderAccount),
            financial_institution_id: // eslint-disable-next-line camelcase
            prevProviderAccount === null || prevProviderAccount === void 0 ? void 0 : (_prevProviderAccount$ = prevProviderAccount.provider_account_state) === null || _prevProviderAccount$ === void 0 ? void 0 : (_prevProviderAccount$2 = _prevProviderAccount$.provider) === null || _prevProviderAccount$2 === void 0 ? void 0 : _prevProviderAccount$2.marketplaceInstitutionId,
            num_financial_portals: numPreviousProviderAccounts,
            // this is the new number since it's already included per above
            primary_source: getPrimarySourceFromProviderAccount(prevProviderAccount)
          }
        });
      }
    }
  }); // This leaves a corner case where there is a combination of user links and unlinks that occur within a single poll


  if (numNextProviderAccounts < numPreviousProviderAccounts) {
    // fun fact: the larger array always has to be first when using `difference` to get a non-empty result
    var unlinkedProviderAccounts = _differenceBy(prevProviderAccounts, nextProviderAccounts, 'provider_account_state.id');

    unlinkedProviderAccounts.forEach(function (pa) {
      var _pa$provider_account_, _pa$provider_account_2;

      analytics.track('financial_account_aggregation_enrollment_changed', {
        enrollment_change_type: numNextProviderAccounts > 0 ? 'decrease_enrollment' : 'unenroll',
        feature_name: 'financial_account_aggregation',
        additional_attributes: {
          financial_portal_id: getProviderAccountStateIdFromProviderAccount(pa),
          financial_institution_id: // eslint-disable-next-line camelcase
          pa === null || pa === void 0 ? void 0 : (_pa$provider_account_ = pa.provider_account_state) === null || _pa$provider_account_ === void 0 ? void 0 : (_pa$provider_account_2 = _pa$provider_account_.provider) === null || _pa$provider_account_2 === void 0 ? void 0 : _pa$provider_account_2.marketplaceInstitutionId,
          num_financial_portals: numNextProviderAccounts,
          primary_source: getPrimarySourceFromProviderAccount(pa)
        }
      });
    });
  }
});